import apiClient from './api';
import type { MaterielDeneignement } from '@/types';

const BASE_URL = '/materiels';

export type EtatMateriel = 'disponible' | 'panne' | 'maintenance';

export interface CreateMaterielDTO {
  nom: string;
  type: string;
  immatriculation?: string;
  etat?: EtatMateriel;
  actif?: boolean;
}

export interface UpdateMaterielDTO extends Partial<CreateMaterielDTO> {}

export interface MaterielFilters {
  type?: string;
  etat?: EtatMateriel;
  actif?: boolean;
}

export interface MaterielStats {
  total: number;
  disponibles: number;
  indisponibles: number;
  parType: Record<string, number>;
}

export const ETAT_MATERIEL_LABELS: Record<EtatMateriel, string> = {
  disponible: 'Disponible',
  panne: 'Panne',
  maintenance: 'Maintenance',
};

/**
 * Service API pour le materiel de deneigement (inclut les vehicules
 * Pony/Tracteur depuis la fusion avec l'ancien referentiel "Engin").
 */
export const materielService = {
  async list(filters: MaterielFilters = {}): Promise<MaterielDeneignement[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await apiClient.get<MaterielDeneignement[]>(
      `${BASE_URL}?${params.toString()}`
    );
    return response.data;
  },

  async get(id: number): Promise<MaterielDeneignement> {
    const response = await apiClient.get<MaterielDeneignement>(`${BASE_URL}/${id}`);
    return response.data;
  },

  async create(data: CreateMaterielDTO): Promise<MaterielDeneignement> {
    const response = await apiClient.post<MaterielDeneignement>(BASE_URL, data);
    return response.data;
  },

  async update(id: number, data: UpdateMaterielDTO): Promise<MaterielDeneignement> {
    const response = await apiClient.put<MaterielDeneignement>(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  async getDisponibles(): Promise<MaterielDeneignement[]> {
    const response = await apiClient.get<MaterielDeneignement[]>(`${BASE_URL}/disponibles`);
    return response.data;
  },

  /**
   * Modifier l'etat operationnel (RG-VH-04).
   */
  async updateEtat(id: number, etat: EtatMateriel): Promise<MaterielDeneignement> {
    const response = await apiClient.patch<MaterielDeneignement>(`${BASE_URL}/${id}/etat`, { etat });
    return response.data;
  },

  async getStats(): Promise<MaterielStats> {
    const response = await apiClient.get<MaterielStats>(`${BASE_URL}/stats/global`);
    return response.data;
  },
};

export default materielService;
